import { FC } from 'react';
import classNames from 'classnames';

import {
  ImageWithTextSideBySideSlice,
  ImageWithTextSideBySideSliceWithBlockquotes,
} from 'prismicio-types';

import { Heading, PrismicImageWithAlt } from '@/components';
import { useFilteredSliceItemsByLocationSlug } from '@/hooks';
import { getContentAlign, getContentVerticalAlign } from '@/utils';
import { resolveImageRadiusToken } from '../../../theme/tokens';

import style from './withblockquotevariation.module.scss';

export const WithBlockquoteVariation: FC<{
  slice: ImageWithTextSideBySideSliceWithBlockquotes &
    ImageWithTextSideBySideSlice;
}> = ({ slice }) => {
  const { filteredItems } = useFilteredSliceItemsByLocationSlug(slice?.items);
  const {
    content_background_color,
    content_full_width,
    content_horizontal_align,
    content_vertical_align,
    image,
    image_corner_preset,
    item_description_color,
    item_title_color,
    layout_direction,
  } = slice.primary;

  const layoutDirection = layout_direction ?? 'left';

  const alignText = content_horizontal_align ?? 'center';

  const bodyVariables = {
    '--content-padding-left': layoutDirection === 'left' ? '13.6%' : '0px',
    '--content-padding-right': layoutDirection === 'left' ? '0px' : '13.6%',
  };

  if (!filteredItems?.length) {
    return null;
  }

  return (
    <>
      <div
        className={classNames(style.body, {
          [style.body__left]: layoutDirection === 'left',
        })}
        style={{
          background: content_background_color as string,
          ...bodyVariables,
        }}
      >
        {filteredItems?.map((feature, index) => (
          <div
            className={classNames(style.body__item_grid_feature, {
              /* eslint-disable-next-line max-len */
              [style.body__item_grid_feature_border_right]:
                layoutDirection === 'left',
            })}
            key={index}
            style={{
              alignItems: getContentAlign(
                content_horizontal_align as
                  | 'start'
                  | 'left'
                  | 'center'
                  | 'end'
                  | 'right',
              ),
              textAlign: alignText,
              alignSelf: getContentVerticalAlign(content_vertical_align),
            }}
          >
            {feature.title && (
              <Heading
                className={style.title}
                level={5}
                style={{ color: item_title_color as string }}
              >
                {feature.title}
              </Heading>
            )}
            {feature.description && (
              <p
                className={style.description}
                style={{ color: item_description_color as string }}
              >
                {feature.description}
              </p>
            )}
          </div>
        ))}
      </div>
      <figure
        className={classNames(
          style.body__figure,
          {
            [style.body__figure__full_width_left]:
              !!content_full_width && layoutDirection === 'left',
            [style.body__figure__full_width_right]:
              !!content_full_width && layoutDirection === 'right',
          },
          resolveImageRadiusToken(image_corner_preset as string),
          {
            [style.body__figure__left]: layoutDirection === 'left',
          },
        )}
      >
        <PrismicImageWithAlt
          field={image}
          fill
          showPlaceholderIfEmpty
          style={{ objectFit: 'cover' }}
        />
      </figure>
    </>
  );
};
